import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { ordersAPI } from '../../services/api';
import apiClient from '../../services/api';
import { Eye, Calendar, Phone, MapPin, Package, Truck, RefreshCw, Send, CheckCircle2, AlertTriangle } from 'lucide-react';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    loadOrders();
  }, [filterStatus]);

  const loadOrders = async () => {
    try {
      const data = await ordersAPI.getAll(filterStatus || null);
      setOrders(data);
    } catch (error) {
      console.error('Failed to load orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await ordersAPI.updateStatus(orderId, newStatus);
      loadOrders();
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
    } catch (error) {
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette commande ? Cette action est irréversible.')) {
      return;
    }
    
    try {
      await ordersAPI.delete(orderId);
      loadOrders();
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder(null);
      }
      alert('Commande supprimée avec succès');
    } catch (error) {
      alert('Erreur lors de la suppression de la commande');
    }
  };

  // ECOTRACK / WorldExpress helpers
  const [ecotrackLoading, setEcotrackLoading] = useState(null);

  const handleEcotrackSend = async (orderId) => {
    setEcotrackLoading(orderId + ':send');
    try {
      const res = await apiClient.post(`/admin/orders/${orderId}/ecotrack/send`);
      alert(`Envoyé à WorldExpress ✓\nTracking : ${res.data.tracking}`);
      loadOrders();
    } catch (e) {
      alert('Échec envoi WorldExpress :\n' + (e.response?.data?.detail || e.message));
    } finally {
      setEcotrackLoading(null);
    }
  };

  const handleEcotrackValidate = async (orderId) => {
    setEcotrackLoading(orderId + ':validate');
    try {
      await apiClient.post(`/admin/orders/${orderId}/ecotrack/validate`);
      alert('Commande validée chez WorldExpress ✓');
      loadOrders();
    } catch (e) {
      alert('Erreur validation : ' + (e.response?.data?.detail || e.message));
    } finally {
      setEcotrackLoading(null);
    }
  };

  const handleEcotrackStatus = async (orderId) => {
    setEcotrackLoading(orderId + ':status');
    try {
      const res = await apiClient.get(`/admin/orders/${orderId}/ecotrack/status`);
      alert(`Statut WorldExpress :\n${JSON.stringify(res.data.info, null, 2)}`);
    } catch (e) {
      alert('Erreur : ' + (e.response?.data?.detail || e.message));
    } finally {
      setEcotrackLoading(null);
    }
  };

  const ecotrackBadge = (order) => {
    const s = order.ecotrack_status;
    if (!s)
      return <span className="text-gray-400 text-xs">— Pas envoyé</span>;
    if (s === 'synced')
      return (
        <span className="inline-flex items-center gap-1 text-cyan-600 text-xs font-semibold">
          <Truck className="w-3 h-3" /> {order.ecotrack_tracking}
        </span>
      );
    if (s === 'validated')
      return (
        <span className="inline-flex items-center gap-1 text-green-600 text-xs font-semibold">
          <CheckCircle2 className="w-3 h-3" /> {order.ecotrack_tracking}
        </span>
      );
    if (s === 'error')
      return (
        <span
          className="inline-flex items-center gap-1 text-red-600 text-xs font-semibold cursor-help"
          title={order.ecotrack_last_error || 'Erreur inconnue'}
        >
          <AlertTriangle className="w-3 h-3" /> Erreur
        </span>
      );
    return <span className="text-gray-400 text-xs">{s}</span>;
  };

  const statusColors = {
    pending: 'bg-yellow-500/20 text-yellow-400',
    confirmed: 'bg-blue-500/20 text-blue-400',
    processing: 'bg-purple-500/20 text-purple-400',
    delivered: 'bg-green-500/20 text-green-400',
    cancelled: 'bg-red-500/20 text-red-400',
    archived: 'bg-gray-500/20 text-gray-400'
  };

  const statusLabels = {
    pending: 'En attente',
    confirmed: 'Confirmée',
    processing: 'En traitement',
    delivered: 'Livrée',
    cancelled: 'Annulée',
    archived: 'Archivée'
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-white">Commandes</h1>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-blue-800 border border-blue-700 rounded-lg px-4 py-2 text-white"
          >
            <option value="">Tous les statuts</option>
            <option value="pending">En attente</option>
            <option value="confirmed">Confirmée</option>
            <option value="processing">En traitement</option>
            <option value="delivered">Livrée</option>
            <option value="cancelled">Annulée</option>
            <option value="archived">Archivée</option>
          </select>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-cyan-400 border-t-transparent"></div>
          </div>
        ) : (
          <div className="bg-blue-800 rounded-lg border border-blue-700 overflow-hidden">
            <table className="w-full">
              <thead className="bg-blue-900">
                <tr>
                  <th className="text-left px-6 py-4 text-gray-300 font-semibold">N° Commande</th>
                  <th className="text-left px-6 py-4 text-gray-300 font-semibold">Client</th>
                  <th className="text-left px-6 py-4 text-gray-300 font-semibold">Total</th>
                  <th className="text-left px-6 py-4 text-gray-300 font-semibold">Statut</th>
                  <th className="text-left px-6 py-4 text-gray-300 font-semibold">Date</th>
                  <th className="text-left px-6 py-4 text-gray-300 font-semibold">WorldExpress</th>
                  <th className="text-right px-6 py-4 text-gray-300 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-blue-700">
                {orders.map((order) => (
                  <tr key={order.id} className="hover:bg-blue-700/30 transition">
                    <td className="px-6 py-4 text-white font-mono text-sm">
                      #{order.id.substring(0, 8).toUpperCase()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-white">{order.customer_name}</div>
                      <div className="text-gray-400 text-sm">{order.phone}</div>
                    </td>
                    <td className="px-6 py-4 text-white font-semibold">
                      {order.total.toLocaleString('fr-DZ')} DA
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={order.status}
                        onChange={(e) => handleStatusChange(order.id, e.target.value)}
                        className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[order.status]}`}
                      >
                        <option value="pending">En attente</option>
                        <option value="confirmed">Confirmée</option>
                        <option value="processing">En traitement</option>
                        <option value="delivered">Livrée</option>
                        <option value="cancelled">Annulée</option>
                        <option value="archived">Archivée</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 text-gray-400 text-sm">
                      {new Date(order.created_at).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-6 py-4">{ecotrackBadge(order)}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex gap-2 justify-end flex-wrap">
                        <button
                          onClick={() => setSelectedOrder(order)}
                          className="p-2 bg-cyan-500 text-white rounded hover:bg-cyan-600 transition"
                          title="Voir détails"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {!order.ecotrack_tracking && (
                          <button
                            onClick={() => handleEcotrackSend(order.id)}
                            disabled={ecotrackLoading === order.id + ':send'}
                            className="p-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition disabled:opacity-50"
                            title="Envoyer à WorldExpress"
                          >
                            {ecotrackLoading === order.id + ':send' ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                          </button>
                        )}
                        {order.ecotrack_tracking && order.ecotrack_status === 'synced' && (
                          <button
                            onClick={() => handleEcotrackValidate(order.id)}
                            disabled={ecotrackLoading === order.id + ':validate'}
                            className="p-2 bg-green-600 text-white rounded hover:bg-green-700 transition disabled:opacity-50"
                            title="Valider chez WorldExpress"
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </button>
                        )}
                        {order.ecotrack_tracking && (
                          <button
                            onClick={() => handleEcotrackStatus(order.id)}
                            disabled={ecotrackLoading === order.id + ':status'}
                            className="p-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition disabled:opacity-50"
                            title="Rafraîchir statut"
                          >
                            <RefreshCw className={`w-4 h-4 ${ecotrackLoading === order.id + ':status' ? 'animate-spin' : ''}`} />
                          </button>
                        )}
                        {order.status === 'delivered' && (
                          <button
                            onClick={() => handleStatusChange(order.id, 'archived')}
                            className="px-3 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition text-sm font-semibold"
                            title="Archiver"
                          >
                            Archiver
                          </button>
                        )}
                        {(order.status === 'archived' || order.status === 'cancelled') && (
                          <button
                            onClick={() => handleDeleteOrder(order.id)}
                            className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition text-sm font-semibold"
                            title="Supprimer"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {orders.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                Aucune commande trouvée
              </div>
            )}
          </div>
        )}

        {/* Order Detail Modal */}
        {selectedOrder && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-blue-800 rounded-lg p-6 max-w-2xl w-full border border-blue-700 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">
                  Commande #{selectedOrder.id.substring(0, 8).toUpperCase()}
                </h2>
                <button onClick={() => setSelectedOrder(null)} className="text-gray-400 hover:text-white">
                  ✕
                </button>
              </div>

              {/* Customer Info */}
              <div className="mb-6 p-4 bg-blue-700 rounded-lg">
                <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Informations Client
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-300">
                    <Phone className="w-4 h-4 text-cyan-400" />
                    {selectedOrder.phone}
                  </div>
                  {selectedOrder.delivery_method === 'delivery' && (
                    <div className="flex items-start gap-2 text-gray-300">
                      <MapPin className="w-4 h-4 text-cyan-400 mt-0.5" />
                      <div>
                        <div>{selectedOrder.address}</div>
                        <div>{selectedOrder.wilaya}</div>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-gray-300">
                    <Calendar className="w-4 h-4 text-cyan-400" />
                    {new Date(selectedOrder.created_at).toLocaleString('fr-FR')}
                  </div>
                </div>
              </div>

              {/* Items */}
              <div className="mb-6">
                <h3 className="text-white font-bold mb-3">Articles</h3>
                <div className="space-y-2">
                  {selectedOrder.items.map((item) => (
                    <div key={item.product_id} className="flex justify-between items-center p-3 bg-blue-700 rounded">
                      <div>
                        <div className="text-white">{item.product_name}</div>
                        <div className="text-gray-400 text-sm">Quantité: {item.quantity}</div>
                      </div>
                      <div className="text-white font-semibold">
                        {(item.price * item.quantity).toLocaleString('fr-DZ')} DA
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Total */}
              <div className="flex justify-between items-center p-4 bg-cyan-500/20 rounded-lg">
                <span className="text-white font-bold text-lg">Total</span>
                <span className="text-cyan-400 font-bold text-2xl">
                  {selectedOrder.total.toLocaleString('fr-DZ')} DA
                </span>
              </div>

              {/* Status Update */}
              <div className="mt-6">
                <label className="block text-white font-semibold mb-2">Changer le statut</label>
                <select
                  value={selectedOrder.status}
                  onChange={(e) => handleStatusChange(selectedOrder.id, e.target.value)}
                  className="w-full bg-blue-700 border border-blue-600 rounded px-4 py-2 text-white"
                >
                  <option value="pending">En attente</option>
                  <option value="confirmed">Confirmée</option>
                  <option value="processing">En traitement</option>
                  <option value="delivered">Livrée</option>
                  <option value="cancelled">Annulée</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Orders;